import {
    ArrowUturnLeftIcon,
    DocumentPlusIcon,
    TrashIcon,
} from "@heroicons/react/24/outline";
import React from "react";
import { EntityActivitySummary } from "./details/entity-activity-summary";
import { ActivityEntityType, EntityActivityEntryProps } from "./types";

interface EntityActivityEntityCreateLabelProps {
    entityType: ActivityEntityType;
}

const EntityActivityEntityCreateLabel: React.FC<
    EntityActivityEntityCreateLabelProps
> = props => {
    const { entityType } = props;

    if (entityType === "Client") {
        return <>created a client.</>;
    }
    if (entityType === "Community") {
        return <>created a community.</>;
    }
    if (entityType === "ReferenceContact") {
        return <>created a contact.</>;
    }
    if (entityType === "ReferenceBusiness") {
        return <>created an organization.</>;
    }

    const exhaustiveCheck: never = entityType;
    throw new Error(`Unhandled entity type: ${exhaustiveCheck}`);
};

interface EntityActivityEntityCreateProps extends EntityActivityEntryProps {
    entityType: ActivityEntityType;
}

export const EntityActivityEntityCreate: React.FC<
    EntityActivityEntityCreateProps
> = props => {
    const { entityType } = props;

    return (
        <div className="relative flex items-start space-x-3">
            <div className="relative px-1">
                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-gray-100 ring-8 ring-white">
                    <DocumentPlusIcon
                        className="h-5 w-5 text-gray-500"
                        aria-hidden="true"
                    />
                </div>
            </div>
            <div className="min-w-0 flex-1 group pt-1">
                <EntityActivitySummary {...props}>
                    <EntityActivityEntityCreateLabel entityType={entityType} />
                </EntityActivitySummary>
            </div>
        </div>
    );
};

interface EntityActivityEntityRestoredLabelProps {
    entityType: ActivityEntityType;
}

const EntityActivityEntityRestoredLabel: React.FC<
    EntityActivityEntityRestoredLabelProps
> = props => {
    const { entityType } = props;

    if (entityType === "Client") {
        return <>restored a client.</>;
    }
    if (entityType === "Community") {
        return <>restored a community.</>;
    }
    if (entityType === "ReferenceContact") {
        return <>restored a contact.</>;
    }
    if (entityType === "ReferenceBusiness") {
        return <>restored an organization.</>;
    }

    const exhaustiveCheck: never = entityType;
    throw new Error(`Unhandled entity type: ${exhaustiveCheck}`);
};

interface EntityActivityEntityRestoredProps extends EntityActivityEntryProps {
    entityType: ActivityEntityType;
}

export const EntityActivityEntityRestored: React.FC<
    EntityActivityEntityRestoredProps
> = props => {
    const { entityType } = props;

    return (
        <div className="relative flex items-start space-x-3">
            <div className="relative px-1">
                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-blue-100 ring-8 ring-white">
                    <ArrowUturnLeftIcon
                        className="h-5 w-5 text-blue-600"
                        aria-hidden="true"
                    />
                </div>
            </div>
            <div className="min-w-0 flex-1 group pt-1">
                <EntityActivitySummary {...props}>
                    <EntityActivityEntityRestoredLabel entityType={entityType} />
                </EntityActivitySummary>
            </div>
        </div>
    );
};

interface EntityActivityEntityDeletedLabelProps {
    entityType: ActivityEntityType;
}

const EntityActivityEntityDeletedLabel: React.FC<
    EntityActivityEntityDeletedLabelProps
> = props => {
    const { entityType } = props;

    if (entityType === "Client") {
        return <>deleted a client.</>;
    }
    if (entityType === "Community") {
        return <>deleted a community.</>;
    }
    if (entityType === "ReferenceContact") {
        return <>deleted a contact.</>;
    }
    if (entityType === "ReferenceBusiness") {
        return <>deleted an organization.</>;
    }

    const exhaustiveCheck: never = entityType;
    throw new Error(`Unhandled entity type: ${exhaustiveCheck}`);
};

interface EntityActivityEntityDeletedProps extends EntityActivityEntryProps {
    entityType: ActivityEntityType;
}

export const EntityActivityEntityDeleted: React.FC<
    EntityActivityEntityDeletedProps
> = props => {
    const { entityType } = props;

    return (
        <div className="relative flex items-start space-x-3">
            <div className="relative px-1">
                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-red-100 ring-8 ring-white">
                    <TrashIcon className="h-5 w-5 text-red-600" aria-hidden="true" />
                </div>
            </div>
            <div className="min-w-0 flex-1 group pt-1">
                <EntityActivitySummary {...props}>
                    <EntityActivityEntityDeletedLabel entityType={entityType} />
                </EntityActivitySummary>
            </div>
        </div>
    );
};
